/**
 * Recordatorio diario (Web Push). En iPhone solo funciona con la app abierta desde el icono de la pantalla de inicio
 * (iOS 16.4+), y el permiso se pide al tocar un botón. El servidor guarda la dirección de envío y la hora; el texto
 * del aviso lo pone el service worker con lo que queda hoy (lo que guarda `saveTodayState`).
 */
import { api } from '../adapters/api-client.js';

export const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

const minutesOf = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return (h ?? 8) * 60 + (m ?? 0); };
const timeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Amsterdam';

function keyBytes(b64u: string): Uint8Array {
  const s = b64u.replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(s + '='.repeat((4 - (s.length % 4)) % 4)), c => c.charCodeAt(0));
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const reg = await Promise.race([navigator.serviceWorker.ready, new Promise<never>((_, no) => setTimeout(() => no(new Error('La app aún se está instalando; vuelve a probar en unos segundos')), 8000))]);
  return reg;
}

/** Activa (o actualiza la hora). Lanza un Error con un texto para el usuario si no se puede. */
export async function enableReminder(time: string): Promise<void> {
  if (!pushSupported()) throw new Error('En iPhone, abre la app desde su icono en la pantalla de inicio para poder activar el recordatorio.');
  const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Sin permiso de notificaciones. Actívalo en Ajustes del iPhone → Notificaciones → Show Up.');
  const reg = await registration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { publicKey } = await api.pushKey();
    if (!publicKey) throw new Error('El servidor aún no tiene configurado el recordatorio');
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey).buffer as ArrayBuffer });
  }
  await api.savePush(sub.toJSON(), minutesOf(time), timeZone());
}

export async function disableReminder(): Promise<void> {
  if (!pushSupported()) return;
  const sub = await (await registration()).pushManager.getSubscription().catch(() => null);
  if (!sub) return;
  await api.deletePush(sub.endpoint).catch(() => { /* ya no estaba */ });
  await sub.unsubscribe().catch(() => {});
}

/** Lo que queda hoy, para el texto del aviso (lo lee el service worker). */
export async function saveTodayState(state: { date: string; fuerza: boolean; wingChun: boolean }): Promise<void> {
  try { await (await caches.open('sut-state')).put('/__sut-state', new Response(JSON.stringify(state), { headers: { 'content-type': 'application/json' } })); }
  catch { /* sin Cache Storage */ }
}
